import { state } from '__common/store';
import { ROOF_VERTEX_SNAP_THRESHOLD_PX } from '__editor/components/roofsSelector/components/roofsSelectorDrawingManager/roofsSelectorDrawingManagerConfigs';

export interface IArrayShapeVertex {
  x: number;
  y: number;
  obstructionId: number;
  cornerIndex: number;
}

let activeArrayShapeSnap: IArrayShapeVertex | null = null;

export function getActiveArrayShapeSnap(): IArrayShapeVertex | null {
  return activeArrayShapeSnap;
}

export function clearActiveArrayShapeSnap() {
  activeArrayShapeSnap = null;
}

// Collects every vertex of every already-drawn array shape (obstruction) on the
// currently selected roof and projects it into stage pixel space (the same
// `coord + canvasCenter` mapping the obstruction model uses) so it can be
// compared against the live cursor.
export function getExistingArrayShapeVerticesInPixels(): IArrayShapeVertex[] {
  const { obstructions: { obstructions }, background: { selectedRoofId }, settings: { canvasCenter } } = state();

  if (!obstructions || !obstructions[selectedRoofId] || !canvasCenter) {
    return [];
  }

  const vertices: IArrayShapeVertex[] = [];
  const roofObstructions = obstructions[selectedRoofId];

  Object.keys(roofObstructions).forEach((obstructionId) => {
    const obstruction = roofObstructions[obstructionId];
    if (!obstruction || !obstruction.coords) {
      return;
    }

    obstruction.coords.forEach((coord: pixelPoint, cornerIndex: number) => {
      vertices.push({
        x: coord.x + canvasCenter.x,
        y: coord.y + canvasCenter.y,
        obstructionId: Number(obstructionId),
        cornerIndex,
      });
    });
  });

  return vertices;
}

// Returns the nearest existing array-shape vertex to `point` if one lies within
// the snap threshold, snapping the point onto it. Otherwise returns `point`
// as-is. The matched vertex (if any) is cached for the snap indicator.
export function snapToArrayShapeVertex(point: pixelPoint): pixelPoint {
  const vertices = getExistingArrayShapeVerticesInPixels();

  if (!vertices.length) {
    activeArrayShapeSnap = null;
    return point;
  }

  let nearest: IArrayShapeVertex | null = null;
  let nearestDistance = ROOF_VERTEX_SNAP_THRESHOLD_PX;

  vertices.forEach((vertex) => {
    const dx = vertex.x - point.x;
    const dy = vertex.y - point.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance <= nearestDistance) {
      nearestDistance = distance;
      nearest = vertex;
    }
  });

  if (nearest) {
    activeArrayShapeSnap = nearest;
    return { x: nearest.x, y: nearest.y };
  }

  activeArrayShapeSnap = null;
  return point;
}
